import { useMemo, useRef, useState } from "react";
import { TbChevronDown, TbSearch } from "react-icons/tb";
import { handleBussinessTaxIDInput, handleTaxIDInput } from "../../../Utils/masks";
import {
  EMPTY_TRAVEL_RULE_FORM,
  TRAVEL_RULE_BENEFICIARY_TYPES,
  TRAVEL_RULE_DESTINATION_COUNTRIES,
  TRAVEL_RULE_OWNERSHIP,
  TRAVEL_RULE_TRANSACTION_PURPOSES,
  TRAVEL_RULE_WALLET_TYPES,
  findTravelRulePurpose,
} from "./travelRuleConstants";

const inputClass = "stables-field-input";

const readOnlyClass =
  "stables-field-input bg-gray-50 text-gray-600 dark:bg-brand-dark-surface dark:text-gray-400";

const selectClass = "stables-field-input appearance-none pr-9";

const FieldLabel = ({ children }) => (
  <span className="mb-1.5 block text-xs font-medium text-gray-500">{children}</span>
);

const TravelRuleSelect = ({ value, onChange, placeholder, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${selectClass} ${!value ? "text-gray-400" : ""}`}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
    <TbChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
  </div>
);

const TransactionPurposeSelect = ({ value, onChange }) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = findTravelRulePurpose(value);

  const filteredPurposes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return TRAVEL_RULE_TRANSACTION_PURPOSES;
    return TRAVEL_RULE_TRANSACTION_PURPOSES.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        item.code.includes(term) ||
        item.category?.toLowerCase().includes(term)
    );
  }, [search]);

  const groupedPurposes = useMemo(() => {
    const groups = [];
    let currentCategory = null;

    filteredPurposes.forEach((item) => {
      if (item.category && item.category !== currentCategory) {
        currentCategory = item.category;
        groups.push({ type: "category", id: `cat-${item.category}`, label: item.category });
      }
      groups.push({ type: "option", ...item });
    });

    return groups;
  }, [filteredPurposes]);

  const handleSelect = (purposeId) => {
    onChange(purposeId);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${selectClass} flex items-center justify-between text-left`}
      >
        <span className={selected ? "text-brand-dark" : "text-gray-400"}>
          {selected ? `${selected.label} (${selected.code})` : "Selecione a finalidade da transação"}
        </span>
        <TbChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-card dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:shadow-none">
            <div className="border-b border-gray-100 p-2 dark:border-brand-dark-border">
              <div className="relative">
                <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar..."
                  className="stables-field-input h-9 pl-9"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {groupedPurposes.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-gray-400">Nenhuma finalidade encontrada.</p>
              ) : (
                groupedPurposes.map((item) =>
                  item.type === "category" ? (
                    <p
                      key={item.id}
                      className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-gray-400"
                    >
                      {item.label}
                    </p>
                  ) : (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleSelect(item.id)}
                      className={`block w-full px-3 py-2 text-left text-sm transition hover:bg-brand-cream dark:hover:bg-[#1f1f1f] ${
                        value === item.id
                          ? "bg-brand-cream font-semibold text-[#c8872a] dark:bg-brand-dark-surface dark:text-brand-gold-mid"
                          : "text-brand-dark"
                      }`}
                    >
                      {item.label} ({item.code})
                    </button>
                  )
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SelfBeneficiaryFields = ({ accountData }) => (
  <div className="space-y-3">
    <label className="block">
      <FieldLabel>Nome do titular</FieldLabel>
      <input type="text" value={accountData.userName || ""} readOnly className={readOnlyClass} />
    </label>
    <label className="block">
      <FieldLabel>Documento do titular</FieldLabel>
      <input type="text" value={accountData.userDocument || ""} readOnly className={readOnlyClass} />
    </label>
  </div>
);

const ExchangeBeneficiaryFields = ({ form, onChange }) => (
  <div className="space-y-3">
    <label className="block">
      <FieldLabel>Razão Social do beneficiário</FieldLabel>
      <input
        type="text"
        value={form.legalName}
        onChange={(e) => onChange({ legalName: e.target.value })}
        placeholder="Digite a razão social"
        className={inputClass}
      />
    </label>
    <label className="block">
      <FieldLabel>CNPJ do beneficiário</FieldLabel>
      <input
        type="text"
        value={form.document}
        onChange={(e) => onChange({ document: handleBussinessTaxIDInput(e.target.value) })}
        placeholder="00.000.000/0000-00"
        className={inputClass}
      />
    </label>
  </div>
);

const ForeignBeneficiaryFields = ({ form, onChange }) => (
  <div className="space-y-3">
    <label className="block">
      <FieldLabel>Nome do beneficiário</FieldLabel>
      <input
        type="text"
        value={form.beneficiaryName}
        onChange={(e) => onChange({ beneficiaryName: e.target.value.replace(/[0-9]+/g, "") })}
        placeholder="Digite o nome"
        className={inputClass}
      />
    </label>
    <label className="block">
      <FieldLabel>ID Internacional do beneficiário</FieldLabel>
      <input
        type="text"
        value={form.internationalId}
        onChange={(e) => onChange({ internationalId: e.target.value })}
        placeholder="Digite o ID Internacional"
        className={inputClass}
      />
    </label>
  </div>
);

const DomesticBeneficiaryFields = ({ form, onChange }) => {
  if (form.beneficiaryType === "domestic_pj") {
    return <ExchangeBeneficiaryFields form={form} onChange={onChange} />;
  }

  return (
    <div className="space-y-3">
      <label className="block">
        <FieldLabel>Nome do beneficiário</FieldLabel>
        <input
          type="text"
          value={form.beneficiaryName}
          onChange={(e) => onChange({ beneficiaryName: e.target.value.replace(/[0-9]+/g, "") })}
          placeholder="Digite o nome"
          className={inputClass}
        />
      </label>
      <label className="block">
        <FieldLabel>CPF do beneficiário</FieldLabel>
        <input
          type="text"
          value={form.document}
          onChange={(e) => onChange({ document: handleTaxIDInput(e.target.value) })}
          placeholder="000.000.000-00"
          className={inputClass}
        />
      </label>
    </div>
  );
};

const ThirdPartyBeneficiaryFields = ({ form, onChange }) => {
  if (form.walletType === "exchange_vasp") {
    return <ExchangeBeneficiaryFields form={form} onChange={onChange} />;
  }

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Tipo do beneficiário</FieldLabel>
        <TravelRuleSelect
          value={form.beneficiaryType}
          onChange={(beneficiaryType) =>
            onChange({
              beneficiaryType,
              legalName: "",
              document: "",
              beneficiaryName: "",
              internationalId: "",
            })
          }
          placeholder="Selecione o tipo do beneficiário"
          options={TRAVEL_RULE_BENEFICIARY_TYPES}
        />
      </div>

      {form.beneficiaryType === "foreign" && (
        <ForeignBeneficiaryFields form={form} onChange={onChange} />
      )}
      {form.beneficiaryType && form.beneficiaryType !== "foreign" && (
        <DomesticBeneficiaryFields form={form} onChange={onChange} />
      )}
    </div>
  );
};

export const StablesTravelRuleFields = ({ form = EMPTY_TRAVEL_RULE_FORM, onChange, accountData = {} }) => {
  const handleChange = (patch) => onChange({ ...form, ...patch });

  const handleOwnershipChange = (ownership) => {
    onChange({
      ...EMPTY_TRAVEL_RULE_FORM,
      ownership,
      destinationCountry: ownership === "self" ? "BR" : form.destinationCountry,
    });
  };

  const handleWalletTypeChange = (walletType) => {
    onChange({
      ...form,
      walletType,
      beneficiaryType: "",
      legalName: "",
      document: "",
      beneficiaryName: "",
      internationalId: "",
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-gray-500">Travel Rule</p>
        <p className="mb-2 mt-1 text-[11px] leading-relaxed text-gray-400">
          Informações exigidas para transferências de ativos digitais conforme regulamentação vigente.
        </p>
      </div>

      <div>
        <FieldLabel>Titularidade da carteira de destino</FieldLabel>
        <TravelRuleSelect
          value={form.ownership}
          onChange={handleOwnershipChange}
          placeholder="Selecione a titularidade da carteira de destino"
          options={TRAVEL_RULE_OWNERSHIP}
        />
      </div>

      {form.ownership && (
        <div>
          <FieldLabel>Tipo da carteira de destino</FieldLabel>
          <TravelRuleSelect
            value={form.walletType}
            onChange={handleWalletTypeChange}
            placeholder="Selecione o tipo da carteira de destino"
            options={TRAVEL_RULE_WALLET_TYPES}
          />
        </div>
      )}

      {form.ownership === "self" && form.walletType && (
        <SelfBeneficiaryFields accountData={accountData} />
      )}

      {form.ownership === "third_party" && form.walletType && (
        <ThirdPartyBeneficiaryFields form={form} onChange={handleChange} />
      )}

      {form.ownership && form.walletType && (
        <>
          <div>
            <FieldLabel>País de destino</FieldLabel>
            <TravelRuleSelect
              value={form.destinationCountry}
              onChange={(destinationCountry) => handleChange({ destinationCountry })}
              placeholder="Selecione o país de destino"
              options={TRAVEL_RULE_DESTINATION_COUNTRIES}
            />
          </div>

          <div>
            <FieldLabel>Finalidade da transação</FieldLabel>
            <TransactionPurposeSelect
              value={form.transactionPurpose}
              onChange={(transactionPurpose) => handleChange({ transactionPurpose })}
            />
          </div>
        </>
      )}
    </div>
  );
};
